import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { createColumnHelper } from '@tanstack/react-table';
import { ShoppingBagIcon } from 'lucide-react';
import { useMemo } from 'react';

import { LocationDTO } from '@invenco/common-interface/accounts';

import {
  DataTable,
  DataTableFilterMenu,
  DataTablePageContent,
  DataTablePagination,
  DataTableRefreshMenu,
  DataTableToolbar,
  useDataTable,
  useDataTableState,
  useRowNavigationCallback,
} from '@/components/data-table';
import { Header, HeaderIcon, HeaderTitle } from '@/components/header';
import { Page as PageLayout } from '@/components/layout';
import { useGateways } from '@/gateways/GatewayProvider';
import { fetchAllPages } from '@/shared/helpers';
import { useQueryWithInput } from '@/shared/hooks/queries';

type ChannelListItem = { id: string; name: string; fulfillmentLocations: string };

const columnHelper = createColumnHelper<ChannelListItem>();

const mapLocationsById = (locationDTOs: LocationDTO[]) => {
  const map = new Map<string, LocationDTO>();
  locationDTOs.forEach((locationDTO) => map.set(locationDTO.id, locationDTO));
  return map;
};

const columns = [
  columnHelper.accessor('name', {
    header: 'Name',
  }),
  columnHelper.accessor('fulfillmentLocations', {
    header: 'Fulfillment locations',
  }),
];

export function ChannelList() {
  const { salesGateway, accountsGateway } = useGateways();
  const onClickRow = useRowNavigationCallback<ChannelListItem>('/sales/channels');

  const { filters, state, onPaginationChange, onSortingChange } = useDataTableState({
    columns,
    filterDefs: {},
  });

  const {
    data: channels,
    isLoading: isLoadingChannels,
    isFetching: isFetchingChannels,
    isPlaceholderData,
    isError: isGetChannelsError,
    refetch: refetchChannels,
  } = useQueryWithInput({
    parentKey: 'channels',
    input: {
      search: filters.search,
    },
    query: (input, { signal }) =>
      fetchAllPages(({ cursor, take }) =>
        salesGateway.getChannels({ search: input.search, cursor, take }, { signal }),
      ),
    placeholderData: keepPreviousData,
  });

  const {
    data: locations,
    isLoading: isLoadingLocations,
    isError: isGetLocationsError,
  } = useQuery({
    queryKey: ['locations'],
    queryFn: ({ signal }) =>
      fetchAllPages(({ cursor, take }) =>
        accountsGateway.getLocations({ cursor, take }, { signal }),
      ),
    select: mapLocationsById,
  });

  const channelListItems = useMemo<ChannelListItem[]>(
    () =>
      channels?.map(({ id, name, fulfillmentLocationIds }) => ({
        id: id, // TODO update ChannelDTO so id is guarenteed
        name,
        fulfillmentLocations: fulfillmentLocationIds
          .map((fulfillmentLocationId) => locations?.get(fulfillmentLocationId)?.name)
          .filter(Boolean)
          .join(', '),
      })) ?? [],
    [channels, locations],
  );

  const table = useDataTable({
    data: channelListItems,
    columns,
    isLoading: isLoadingChannels || isLoadingLocations,
    isLoadingWithStaleData: isFetchingChannels && isPlaceholderData,
    hasError: isGetChannelsError || isGetLocationsError,
    manualPagination: false,
    manualSorting: false,
    autoResetPageIndex: false,
    onPaginationChange,
    onSortingChange,
    state,
  });

  return (
    <PageLayout>
      <Header>
        <HeaderIcon>
          <ShoppingBagIcon />
        </HeaderIcon>
        <HeaderTitle>Channels</HeaderTitle>
      </Header>
      <DataTablePageContent>
        <DataTableToolbar>
          <DataTableFilterMenu filters={filters} />
          <DataTableRefreshMenu onRefresh={refetchChannels} />
        </DataTableToolbar>
        <DataTable table={table} onClickRow={onClickRow} />
        <DataTablePagination table={table} />
      </DataTablePageContent>
    </PageLayout>
  );
}
